import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowRight, TrendingUp, LayoutGrid, RefreshCcw, Layers, CheckCircle2, Target, Zap, BarChart3 } from "lucide-react";
import {
  sectionVariant, heroVariant, staggerContainer, itemVariant, diagramVariant,
  viewportOnce, viewportOnceNear, glowVariant,
} from "@/lib/motion";
import { usePageContent } from "@/hooks/usePageContent";

const iconMap: Record<string, any> = { TrendingUp, LayoutGrid, RefreshCcw, Layers };

const fallback = {
  heroLabel: "Growth Systems",
  heroHeadlineLine1: "Four systems.",
  heroHeadlineLine2: "One compounding outcome.",
  heroDescription: "We don't sell campaigns. We build interconnected systems that compound. Acquisition feeds conversion, conversion funds retention, retention enables scale.",
  heroCta: "Discuss your growth system",
  heroSecondaryCta: "View case studies",
  flowLabel: "How It Works",
  flowTitle: "Systems that feed each other",
  flowDescription: "Each system strengthens the next. The result is growth that accelerates, not growth that plateaus.",
  systems: [
    {
      number: "01", title: "Acquire", tagline: "Reach the right people, at the right moment", icon: "TrendingUp",
      problemFrame: "Why most acquisition fails",
      hardTruth: "Most acquisition fails because brands optimize for reach when they should optimize for intent. Volume without precision is just expensive noise.",
      approach: "We flip the equation. Precision over volume, intent signals over impressions. The goal isn't more traffic. It's traffic that converts.",
      capabilities: [
        { name: "Paid Search (SEM)", description: "Capture demand at the moment of search intent", link: "/services" },
        { name: "Paid Social", description: "Precision targeting across Meta, LinkedIn, TikTok", link: "/services" },
        { name: "Programmatic Display", description: "Scale reach with audience-first buying", link: "/services" },
        { name: "SEO & Content", description: "Organic visibility that compounds over time", link: "/seo" },
      ],
      outcomes: ["Lower CAC", "Higher-quality traffic", "Sustainable economics"],
      metrics: { label: "Avg. CAC Reduction", value: "32%" },
    },
    {
      number: "02", title: "Convert", tagline: "Turn attention into measurable action", icon: "LayoutGrid",
      problemFrame: "Why conversion stalls",
      hardTruth: "Conversion problems are rarely UX problems. They're clarity problems. The gap between click and customer is where most budgets die.",
      approach: "We obsess over this space until the path to purchase feels inevitable. Not guesswork. Structured testing against clear hypotheses.",
      capabilities: [
        { name: "Conversion Rate Optimization", description: "Data-driven testing and iteration cycles", link: "/services" },
        { name: "Landing Page Design", description: "High-performance pages built for conversion", link: "/services" },
        { name: "Website Development", description: "Custom websites engineered for performance and growth", link: "/web-development" },
        { name: "UX Strategy", description: "Remove friction from the customer journey", link: "/services" },
        { name: "Creative Development", description: "Messaging that moves people to act", link: "/services" },
      ],
      outcomes: ["Higher conversion", "Improved ROAS", "Recovered revenue"],
      metrics: { label: "Avg. CVR Lift", value: "48%" },
    },
    {
      number: "03", title: "Retain", tagline: "Revenue beyond the first purchase", icon: "RefreshCcw",
      problemFrame: "Why retention breaks",
      hardTruth: "Retention is not automation. It's sequencing. Wrong timing kills more programs than wrong messaging.",
      approach: "Acquisition is a cost. Retention is a profit center. This is where unit economics become sustainable and compounding begins.",
      capabilities: [
        { name: "Email Marketing", description: "Segmented, automated, revenue-generating", link: "/email-marketing" },
        { name: "Lifecycle Campaigns", description: "Right message at the right moment", link: "/lifecycle-marketing" },
        { name: "Loyalty Programs", description: "Incentive structures that increase LTV", link: "/loyalty-programs" },
        { name: "CRM Strategy", description: "Unified view of the customer relationship", link: "/crm-strategy" },
      ],
      outcomes: ["Higher LTV", "Lower churn", "Compounding revenue"],
      metrics: { label: "Avg. LTV Increase", value: "2.4x" },
    },
    {
      number: "04", title: "Scale", tagline: "Sustainable momentum that compounds", icon: "Layers",
      problemFrame: "Why scaling fails",
      hardTruth: "Scaling isn't spending more. It's building infrastructure for faster, better decisions. Without measurement, you're just hoping.",
      approach: "Data, attribution, automation. The foundation that turns growth from hope into predictable outcome. Infrastructure before scale.",
      capabilities: [
        { name: "Analytics & Measurement", description: "Clear visibility into what works", link: "/analytics" },
        { name: "Attribution Modeling", description: "True path to conversion understanding", link: "/services" },
        { name: "Data Infrastructure", description: "Clean data for confident decisions", link: "/services" },
        { name: "Growth Strategy", description: "Roadmap for sustainable scale", link: "/services" },
      ],
      outcomes: ["Clear measurement", "Confident allocation", "Predictable growth"],
      metrics: { label: "Avg. Efficiency Gain", value: "41%" },
    },
  ],
  integrationLabel: "The Compound Effect",
  integrationTitle: "These systems work together",
  integrationDescription: "Most engagements start with one system, acquisition or conversion, and expand as we prove value. The magic happens when all four systems are aligned and feeding each other.",
  integrationBenefits: [
    { icon: "Target", title: "Unified Strategy", description: "Every channel, every touchpoint working toward the same outcome." },
    { icon: "Zap", title: "Faster Iteration", description: "Insights from one system immediately improve another." },
    { icon: "BarChart3", title: "Compounding Returns", description: "Growth that accelerates over time, not growth that plateaus." },
  ],
  finalCtaTitle: "Ready to build your growth system?",
  finalCtaDescription: "Let's start with a conversation about where you are and where you want to go.",
  finalCtaButtonText: "Start the conversation",
};

const benefitIcons: Record<string, any> = { Target, Zap, BarChart3 };

const Services = () => {
  const { content } = usePageContent("services", fallback);
  const systems = content.systems;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20 md:pt-32 lg:pt-40">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <motion.div className="absolute top-0 left-1/4 w-[800px] h-[800px] rounded-full" initial="hidden" animate="visible" variants={glowVariant} style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.06) 0%, transparent 70%)' }} />
            <motion.div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] rounded-full" initial="hidden" animate="visible" variants={glowVariant} style={{ background: 'radial-gradient(circle, hsl(var(--accent) / 0.04) 0%, transparent 70%)' }} />
          </div>
          <div className="container-wide relative z-10">
            <motion.div className="max-w-4xl" initial="hidden" animate="visible" variants={staggerContainer}>
              <motion.span variants={heroVariant} className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-6 block">{content.heroLabel}</motion.span>
              <motion.h1 variants={heroVariant} className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground mb-6 leading-[1.1]">
                {content.heroHeadlineLine1}<br /><span className="text-muted-foreground">{content.heroHeadlineLine2}</span>
              </motion.h1>
              <motion.p variants={heroVariant} className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl mb-8">{content.heroDescription}</motion.p>
              <motion.div variants={heroVariant} className="flex flex-col sm:flex-row items-start gap-4">
                <Button size="lg" className="group" asChild><Link to="/contact">{content.heroCta}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link></Button>
                <Button variant="ghost" size="lg" asChild><Link to="/case-studies">{content.heroSecondaryCta}</Link></Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* System Flow Diagram */}
        <section className="py-12 md:py-20 lg:py-32">
          <div className="container-wide">
            <motion.div className="mb-8 md:mb-12 lg:mb-16" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4 block">{content.flowLabel}</span>
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-foreground mb-4">{content.flowTitle}</h2>
              <p className="text-muted-foreground max-w-xl">{content.flowDescription}</p>
            </motion.div>
            <motion.div className="relative" initial="hidden" whileInView="visible" viewport={viewportOnceNear} variants={diagramVariant}>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-4">
                {systems.map((system: any, index: number) => {
                  const IconComponent = iconMap[system.icon] || Target;
                  return (
                    <div key={system.title} className="relative group">
                      <div className="bg-muted/30 border border-border p-5 md:p-6 lg:p-8 transition-all duration-300 hover:bg-muted/50 hover:border-primary/20 h-full">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"><IconComponent className="h-5 w-5 text-primary" strokeWidth={1.5} /></div>
                          <span className="text-xs text-muted-foreground font-mono">{system.number}</span>
                        </div>
                        <h3 className="font-display text-xl font-semibold text-foreground mb-2">{system.title}</h3>
                        <p className="text-sm text-muted-foreground mb-4">{system.tagline}</p>
                        <div className="pt-4 border-t border-border">
                          <span className="text-xs text-muted-foreground uppercase tracking-wider">{system.metrics.label}</span>
                          <p className="text-2xl font-display font-semibold text-foreground mt-1">{system.metrics.value}</p>
                        </div>
                      </div>
                      {index < systems.length - 1 && (
                        <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10"><ArrowRight className="h-4 w-4 text-muted-foreground/50" /></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Detailed Systems */}
        {systems.map((system: any, systemIndex: number) => {
          const IconComponent = iconMap[system.icon] || Target;
          const isEven = systemIndex % 2 === 0;
          return (
            <section key={system.title} id={system.title.toLowerCase()} className={`py-12 md:py-20 lg:py-32 ${isEven ? 'bg-background' : 'bg-muted/20'}`}>
              <div className="container-wide">
                <motion.div className="grid lg:grid-cols-12 gap-8 lg:gap-20" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={staggerContainer}>
                  <motion.div className="lg:col-span-5" variants={sectionVariant}>
                    <div className="sticky top-32">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center"><IconComponent className="h-6 w-6 text-primary" strokeWidth={1.5} /></div>
                        <span className="text-sm font-mono text-muted-foreground">{system.number}</span>
                      </div>
                      <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-3">{system.title}</h2>
                      <p className="text-lg text-muted-foreground mb-8">{system.tagline}</p>
                      <div className="mb-8">
                        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium mb-3 block">{system.problemFrame}</span>
                        <p className="text-foreground leading-relaxed">{system.hardTruth}</p>
                      </div>
                      <div className="mb-8 p-6 bg-primary/5 border-l-2 border-primary">
                        <span className="text-xs uppercase tracking-[0.15em] text-primary font-medium mb-3 block">Our Approach</span>
                        <p className="text-foreground leading-relaxed text-sm">{system.approach}</p>
                      </div>
                      <div>
                        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium mb-4 block">Outcomes</span>
                        <div className="flex flex-wrap gap-2">
                          {system.outcomes.map((outcome: string) => (
                            <span key={outcome} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-muted/50 text-sm text-foreground border border-border">
                              <CheckCircle2 className="h-3.5 w-3.5 text-primary" />{outcome}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                  <motion.div className="lg:col-span-7" variants={staggerContainer}>
                    <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium mb-6 block">Capabilities</span>
                    <div className="space-y-4">
                      {system.capabilities.map((capability: any) => (
                        <motion.div key={capability.name} variants={itemVariant}>
                          <Link to={capability.link} className="block group">
                            <div className="p-6 bg-background border border-border transition-all duration-300 hover:border-primary/30 hover:bg-muted/30">
                              <div className="flex items-start justify-between gap-4">
                                <div>
                                  <h4 className="font-display text-lg font-medium text-foreground mb-2 group-hover:text-primary transition-colors">{capability.name}</h4>
                                  <p className="text-muted-foreground text-sm">{capability.description}</p>
                                </div>
                                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                              </div>
                            </div>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                </motion.div>
              </div>
            </section>
          );
        })}

        {/* Integration Section */}
        <section className="py-12 md:py-20 lg:py-32 bg-background relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] rounded-full" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={glowVariant} style={{ background: 'radial-gradient(circle, hsl(var(--primary) / 0.04) 0%, transparent 60%)' }} />
          </div>
          <div className="container-wide relative z-10">
            <motion.div className="max-w-3xl mx-auto text-center mb-8 md:mb-12 lg:mb-16" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-6 block">{content.integrationLabel}</span>
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-6">{content.integrationTitle}</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">{content.integrationDescription}</p>
            </motion.div>
            <motion.div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto" initial="hidden" whileInView="visible" viewport={viewportOnceNear} variants={staggerContainer}>
              {content.integrationBenefits.map((benefit: any) => {
                const IconComp = benefitIcons[benefit.icon] || Target;
                return (
                  <motion.div key={benefit.title} variants={itemVariant} className="text-center p-5 md:p-6 lg:p-8 bg-muted/30 border border-border">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4"><IconComp className="h-6 w-6 text-primary" strokeWidth={1.5} /></div>
                    <h3 className="font-display text-lg font-semibold text-foreground mb-2">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">{benefit.description}</p>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-12 md:py-20 lg:py-32 bg-muted/20 border-t border-border">
          <div className="container-wide">
            <motion.div className="max-w-2xl mx-auto text-center" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-6">{content.finalCtaTitle}</h2>
              <p className="text-lg text-muted-foreground mb-8">{content.finalCtaDescription}</p>
              <Button size="lg" className="group" asChild><Link to="/contact">{content.finalCtaButtonText}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link></Button>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Services;
